import './CompanyForm.css';
import React from 'react';
import GlobalProps from '../../../components/globalProps';
import { patchCompany, CompanyBase, addCompany } from '../../../model/company';
import { Label } from '../../../components/Form/Label/Label';
import { getCountries } from '../../../model/country';
import { useRemoteData } from '../../../model/data';
import { sortBy } from 'lodash';
import { handleError } from '../../../model/notification';
import { isAdmin, useSelectedMembership } from '../../../model/membership';
import { t } from '../../../locales/translate';
import { objectToOptions } from '../../../util/util';
import { Stack } from '../../../components/Stack/Stack';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../components/Form/FormForm';
import { FormTextField } from '../../../components/Form/TextField/FormTextField';
import { FormPercentageField } from '../../../components/Form/TextField/FormPercentageField';
import { FormSelect } from '../../../components/Form/Select/FormSelect';
import { FormCheckbox } from '../../../components/Form/Checkbox/FormCheckbox';
import { FormTagsInput } from '../../../components/Form/TagsInput/FormTagsInput';

interface Props extends GlobalProps {
  company: CompanyAddModel;
  onClose: () => void;
}

interface CompanyAddModel extends CompanyBase {
  ticketing?: boolean;
  merch?: boolean;
  address: {
    address?: string | null;
    postCode?: string | null;
    city?: string | null;
    countryCode?: string | null;
    phone?: string | null;
    email?: string | null;
  };
}

export function CompanyForm({ company, onClose }: Props): JSX.Element {
  const { model, fields, valid, dirty, onSubmit, submissionStatus } = useForm<CompanyAddModel>({
    model: {
      ...company,
      ticketing: company.features?.includes('ticketing') || false,
      merch: company.features?.includes('merch') || false,
      eventPayoutCycle: company.eventPayoutCycle || 'monthly',
    },
    onSubmit: async () => {
      const submitModel = {
        ...model,
        features: [...(model.ticketing ? ['ticketing'] : []), ...(model.merch ? ['merch'] : [])],
      };

      if (model.id) {
        await patchCompany(model.id, submitModel);
      } else {
        await addCompany(submitModel);
      }
      onClose();
    },
    onSubmitError: (error) => handleError(error),
    validations: {
      name: 'required',
      address: {
        address: 'required',
      },
    },
  });

  const membership = useSelectedMembership();

  const [countries] = useRemoteData(getCountries, (db) => {
    return db.table('countries').all;
  });

  return (
    <FormForm
      onSubmit={onSubmit}
      className="companies-modal__form"
      canSubmit={dirty && valid}
      onClose={onClose}
      loading={submissionStatus === 'submitting'}
    >
      <Stack>
        <FormTextField {...fields.name} label={t.model.user.name} />
        <div />
        <Label title={t.model.address._name} />
        <FormTextField {...fields.address.fields.address} label={t.model.address._name} />
        <FormTextField {...fields.address.fields.postCode} label={t.model.address.postCode} />
        <FormTextField {...fields.address.fields.city} label={t.model.address.city} />
        <FormSelect
          label={t.model.address.country}
          {...fields.address.fields.countryCode}
          options={sortBy(countries, 'name').map((c) => ({
            label: c.name,
            value: c.countryCode,
          }))}
          allowEmpty
        />
        <FormTextField {...fields.address.fields.phone} label={t.model.user.phone} />
        <FormTextField {...fields.address.fields.email} label={t.model.user.email} />
        <div />
        <Label title={t.model.invoice.invoicing} />
        <FormSelect
          label={t.model.invoice.invoicingCycle}
          {...fields.invoicingCycle}
          options={objectToOptions(t.model.invoice.invoicingCycles)}
          allowEmpty
        />
        <FormSelect
          label={t.model.invoice.eventPayoutCycle}
          {...fields.eventPayoutCycle}
          options={objectToOptions(t.model.invoice.invoicingCycles)}
        />
        <FormPercentageField
          {...fields.defaultEventPayoutPercentage}
          label={t.model.invoice.defaultEventPayoutPercentage}
          min={0}
          max={100}
          placeholder="80"
        />
        <FormTagsInput {...fields.billingEmails} label={t.model.invoice.billingEmails} />

        {isAdmin(membership) && (
          <>
            <div />
            <Label title="Features" />
            <FormCheckbox {...fields.ticketing} label={t.model.ticket.ticketing} />
            <FormCheckbox {...fields.merch} label={t.model.product.merch} />
          </>
        )}
      </Stack>
    </FormForm>
  );
}
